import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import { Reveal, Overline } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const res = await fetch(`${API}/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (!res.ok) throw new Error();
      toast.success("Bericht verzonden. Bedankt voor je reactie!");
      setForm({ name: "", email: "", message: "" });
    } catch {
      toast.error("Verzenden is niet gelukt. Probeer het later opnieuw.");
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "min-h-[48px] w-full rounded-xl border border-gooi-green/20 bg-white px-4 text-base text-gooi-green placeholder:text-gooi-green/40 transition-colors duration-300 focus:border-gooi-green focus:outline-none";

  return (
    <section id="contact" data-testid="contact-section" className="scroll-mt-24 py-24 md:py-32">
      <div className="mx-auto max-w-[90rem] px-5 md:px-12">
        <div className="grid gap-12 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <Overline>Contact</Overline>
              <h2 className="mt-4 font-display text-3xl font-medium leading-tight tracking-tight text-gooi-green md:text-5xl">
                Vragen of meedoen?
              </h2>
              <p className="mt-5 max-w-md text-base leading-relaxed text-gooi-green/70">
                Stuur ons een bericht. Of je nu een vraag hebt, mee wilt denken of de campagne wilt steunen als
                vrijwilliger: we reageren zo snel mogelijk.
              </p>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <form
                onSubmit={onSubmit}
                data-testid="contact-form"
                className="rounded-2xl bg-gooi-sand p-6 md:p-10"
                noValidate={false}
              >
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label htmlFor="contact-name" className="mb-2 block text-sm font-semibold text-gooi-green">
                      Naam
                    </label>
                    <input
                      id="contact-name"
                      name="name"
                      data-testid="contact-name-input"
                      required
                      maxLength={120}
                      value={form.name}
                      onChange={onChange}
                      placeholder="Je naam"
                      className={inputCls}
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="mb-2 block text-sm font-semibold text-gooi-green">
                      E-mailadres
                    </label>
                    <input
                      id="contact-email"
                      name="email"
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={form.email}
                      onChange={onChange}
                      placeholder="jij@voorbeeld.nl"
                      className={inputCls}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <label htmlFor="contact-message" className="mb-2 block text-sm font-semibold text-gooi-green">
                    Bericht
                  </label>
                  <textarea
                    id="contact-message"
                    name="message"
                    data-testid="contact-message-input"
                    required
                    rows={5}
                    maxLength={4000}
                    value={form.message}
                    onChange={onChange}
                    placeholder="Waar kunnen we je mee helpen?"
                    className={`${inputCls} py-3`}
                  />
                </div>
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="mt-6 inline-flex min-h-[48px] items-center gap-2 rounded-full bg-gooi-green px-7 font-display text-base font-semibold tracking-tight text-gooi-offwhite transition-colors duration-300 hover:bg-gooi-forest disabled:opacity-60"
                >
                  {sending ? (
                    <Loader2 className="h-5 w-5 animate-spin" strokeWidth={1.5} aria-hidden="true" />
                  ) : (
                    <Send className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
                  )}
                  {sending ? "Verzenden…" : "Verstuur bericht"}
                </button>
                <p className="mt-4 text-xs leading-relaxed text-gooi-green/55">
                  We gebruiken je gegevens alleen om op je bericht te reageren. Lees meer op de{" "}
                  <a href="/privacy" className="underline decoration-gooi-green/30 underline-offset-4 hover:decoration-gooi-green">
                    privacy-pagina
                  </a>
                  .
                </p>
              </form>
            </Reveal>
          </div>
        </div>
      </div>
    </section>
  );
};
